import { Link } from "react-router-dom";
import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import {
  networkStats,
  architecturePoPs,
  routingFeatures,
  protocols,
  codecs,
  monitoringTools,
  supportTiers,
  architectureLayers,
  liveMetrics,
} from "@/mocks/network";

const popColorMap: Record<string, { dot: string; ring: string; badge: string; border: string }> = {
  primary: { dot: "bg-primary-500", ring: "ring-primary-400", badge: "bg-primary-100 text-primary-800", border: "border-l-primary-500" },
  accent: { dot: "bg-accent-500", ring: "ring-accent-400", badge: "bg-accent-100 text-accent-800", border: "border-l-accent-500" },
  secondary: { dot: "bg-secondary-500", ring: "ring-secondary-400", badge: "bg-secondary-100 text-secondary-800", border: "border-l-secondary-500" },
};

export default function NetworkQuality() {
  return (
    <div className="min-h-screen bg-background-50 text-foreground-950">
      <Header />
      <main>
        {/* Hero */}
        <section className="pt-28 md:pt-36 pb-10 md:pb-14 overflow-hidden relative">
          <div className="absolute inset-0 vx-grid-bg pointer-events-none" />
          <div className="absolute -top-32 -right-32 w-[400px] h-[400px] rounded-full bg-accent-100/60 blur-3xl pointer-events-none" />
          <div className="absolute -bottom-20 -left-20 w-[300px] h-[300px] rounded-full bg-primary-100/50 blur-3xl pointer-events-none" />
          <div className="relative max-w-7xl mx-auto px-4 md:px-6">
            <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-5">
              <Link to="/" className="hover:text-primary-600 cursor-pointer">Home</Link>
              <i className="ri-arrow-right-s-line w-3 h-3 flex items-center justify-center" />
              <span className="text-foreground-800">Network & Quality</span>
            </nav>
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-accent-100 text-accent-800 text-xs font-medium mb-5">
              4 PoPs · 700+ interconnects · 99.99% uptime
            </span>
            <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-semibold text-foreground-950 leading-tight tracking-tight max-w-4xl">
              Carrier-grade network. Verified quality.
            </h1>
            <p className="mt-4 text-base md:text-lg text-foreground-700 max-w-3xl leading-relaxed">
              Four points of presence across three continents, multi-transit routing at every site, and real-time quality monitoring that auto-corrects before you notice.
            </p>

            {/* Live metric cards */}
            <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
              {liveMetrics.slice(0, 6).map((m) => (
                <div key={m.label} className="rounded-lg border border-background-200 bg-background-50 p-3 md:p-4">
                  <p className="text-[11px] uppercase tracking-wider text-foreground-500 font-medium">{m.label}</p>
                  <p className="mt-1 text-xl md:text-2xl font-semibold text-foreground-950 font-heading">{m.value}</p>
                  <p className={`text-[11px] mt-0.5 flex items-center gap-1 ${m.positive ? "text-accent-700" : "text-foreground-500"}`}>
                    <i className={`${m.trend === "→" ? "ri-arrow-right-line" : m.positive ? "ri-arrow-up-line" : "ri-arrow-down-line"} w-3 h-3 flex items-center justify-center`} />
                    {m.trend} {m.period}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Stats row */}
        <section className="pb-10 md:pb-14">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-0 border border-background-200 rounded-xl overflow-hidden bg-background-50">
              {networkStats.map((s) => (
                <div key={s.label} className="p-5 border-b border-r border-background-200 last:border-r-0 text-center">
                  <p className="text-2xl md:text-3xl font-semibold text-foreground-950 font-heading">{s.value}</p>
                  <p className="text-xs text-foreground-600 mt-1 leading-tight">{s.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Architecture — global PoP map visualization */}
        <section className="py-14 md:py-20 bg-background-100/60 border-y border-background-200">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="mb-10">
              <span className="text-xs uppercase tracking-widest text-accent-700 font-semibold">Architecture</span>
              <h2 className="mt-3 text-3xl md:text-4xl font-semibold text-foreground-950 leading-tight">
                Four PoPs. Three continents. One fabric.
              </h2>
              <p className="mt-3 text-foreground-700 max-w-3xl leading-relaxed">
                Each point of presence operates with redundant SBCs, multiple transit providers, and independent power and cooling. The control plane synchronizes routing state across all PoPs in sub-second intervals.
              </p>
            </div>

            {/* Architecture layers */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-12">
              {architectureLayers.map((layer) => (
                <div key={layer.name} className="rounded-lg border border-background-200 bg-background-50 p-5">
                  <p className="text-xs font-semibold uppercase tracking-wider text-foreground-500">{layer.name}</p>
                  <p className="mt-2 text-sm text-foreground-700 leading-relaxed">{layer.desc}</p>
                  <p className="mt-3 text-xl font-semibold text-primary-600 font-heading">{layer.count}</p>
                </div>
              ))}
            </div>

            {/* PoP cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {architecturePoPs.map((pop) => {
                const pc = popColorMap[pop.color];
                return (
                  <div key={pop.region} className={`rounded-xl border border-background-200 bg-background-50 p-6 hover:ring-1 ${pc.ring} transition-all`}>
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <span className={`text-[11px] px-2 py-1 rounded-full font-medium ${pc.badge}`}>{pop.region}</span>
                        <p className="mt-2 text-sm font-semibold text-foreground-800">{pop.city}</p>
                      </div>
                      <span className={`w-10 h-10 rounded-full ${pc.dot} flex items-center justify-center shrink-0`}>
                        <i className="ri-building-4-line text-background-50 text-lg w-5 h-5 flex items-center justify-center" />
                      </span>
                    </div>

                    <div className="space-y-3 text-sm">
                      {pop.carrierHotels && (
                        <div>
                          <p className="text-xs text-foreground-500 font-medium uppercase tracking-wider">Carrier hotels</p>
                          <p className="mt-0.5 text-foreground-700 leading-relaxed">{pop.carrierHotels}</p>
                        </div>
                      )}
                      <div>
                        <p className="text-xs text-foreground-500 font-medium uppercase tracking-wider">Transit providers</p>
                        <div className="mt-1 flex flex-wrap gap-1.5">
                          {pop.transits.map((t) => (
                            <span key={t} className="text-[11px] px-2 py-0.5 rounded-full bg-background-100 text-foreground-700 border border-background-200">{t}</span>
                          ))}
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-2 pt-1 border-t border-background-200">
                        <div>
                          <p className="text-xs text-foreground-500">Peering</p>
                          <p className="text-xs font-medium text-foreground-800 mt-0.5">{pop.peering}</p>
                        </div>
                        <div>
                          <p className="text-xs text-foreground-500">Codecs</p>
                          <p className="text-xs font-medium text-foreground-800 mt-0.5">{pop.codecs}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Routing & Redundancy */}
        <section className="py-14 md:py-20">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="mb-10">
              <span className="text-xs uppercase tracking-widest text-primary-600 font-semibold">Routing & Redundancy</span>
              <h2 className="mt-3 text-3xl md:text-4xl font-semibold text-foreground-950 leading-tight">
                Every call finds the best path. Automatically.
              </h2>
              <p className="mt-3 text-foreground-700 max-w-3xl leading-relaxed">
                Our LCR engine evaluates multiple routes per destination in real time, factoring in ASR, PDD, and cost. Routes degrade? Traffic shifts mid-call-attempt. No manual intervention, no quality dips.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {routingFeatures.map((f) => (
                <div key={f.title} className="rounded-xl border border-background-200 bg-background-50 p-6 hover:border-background-300 transition-colors">
                  <span className="w-11 h-11 rounded-lg bg-accent-100 text-accent-700 flex items-center justify-center">
                    <i className={`${f.icon} text-xl w-6 h-6 flex items-center justify-center`} />
                  </span>
                  <h3 className="mt-4 text-base font-semibold text-foreground-950 font-heading">{f.title}</h3>
                  <p className="mt-2 text-sm text-foreground-600 leading-relaxed">{f.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Live metrics deeper dive */}
        <section className="py-14 md:py-20 bg-background-100/60 border-y border-background-200">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
              <div>
                <span className="text-xs uppercase tracking-widest text-accent-700 font-semibold">Verified metrics</span>
                <h2 className="mt-3 text-3xl md:text-4xl font-semibold text-foreground-950 leading-tight">
                  Real data. Real network.
                </h2>
                <p className="mt-4 text-foreground-700 leading-relaxed max-w-lg">
                  Every metric shown here is pulled from our live monitoring stack — aggregated across all PoPs and all destinations for the rolling 30-day window. No marketing fluff, no cherry-picked samples.
                </p>
                <div className="mt-6 rounded-lg border border-background-200 bg-background-50 p-5 flex items-start gap-3">
                  <i className="ri-information-line text-accent-600 w-5 h-5 flex items-center justify-center mt-0.5" />
                  <div>
                    <p className="text-xs font-semibold text-foreground-800">How we measure</p>
                    <p className="text-xs text-foreground-600 mt-0.5 leading-relaxed">
                      ASR = (answered calls / total call attempts) × 100. PDD = time from INVITE to 183 Session Progress. MOS = Mean Opinion Score computed from R-Factor per ITU-T G.107 (E-model) across all RTP streams. Metrics are refreshed every 10 minutes from live telemetry.
                    </p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {liveMetrics.map((m) => (
                  <div key={m.label} className="rounded-lg border border-background-200 bg-background-50 p-5">
                    <p className="text-xs text-foreground-500 font-medium uppercase tracking-wider">{m.label}</p>
                    <p className="mt-1 text-2xl font-semibold text-foreground-950 font-heading">{m.value}</p>
                    <p className={`text-xs mt-1 flex items-center gap-1 ${m.positive ? "text-accent-700" : "text-foreground-500"}`}>
                      <i className={`${m.trend === "→" ? "ri-arrow-right-line" : m.positive ? "ri-arrow-up-line" : "ri-arrow-down-line"} w-3 h-3 flex items-center justify-center`} />
                      {m.trend}
                      <span className="text-foreground-400 ml-1">{m.period}</span>
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Protocols & Codecs */}
        <section className="py-14 md:py-20">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="mb-10">
              <span className="text-xs uppercase tracking-widest text-primary-600 font-semibold">Protocols & Codecs</span>
              <h2 className="mt-3 text-3xl md:text-4xl font-semibold text-foreground-950 leading-tight">
                Speak any protocol. Transcode any codec.
              </h2>
              <p className="mt-3 text-foreground-700 max-w-3xl leading-relaxed">
                Full SIP/SDP interoperability with all major switch and SBC vendors. TLS + SRTP by default. Codec transcoding performed at the egress SBC with sub-10ms overhead.
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Protocols */}
              <div>
                <h3 className="text-sm font-semibold text-foreground-800 uppercase tracking-wider mb-4 flex items-center gap-2">
                  <i className="ri-exchange-line text-primary-600 w-4 h-4 flex items-center justify-center" />
                  Signaling & Media Protocols
                </h3>
                <div className="rounded-xl border border-background-200 overflow-hidden">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-background-200 text-left text-xs uppercase tracking-wider text-foreground-500 bg-background-100/60">
                        <th className="px-4 py-3 font-medium">Protocol</th>
                        <th className="px-4 py-3 font-medium">Version</th>
                        <th className="px-4 py-3 font-medium">Transport</th>
                        <th className="px-4 py-3 font-medium">Category</th>
                        <th className="px-4 py-3 font-medium">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {protocols.map((p) => (
                        <tr key={p.name} className="border-b border-background-200 last:border-0 hover:bg-background-100/40 transition-colors">
                          <td className="px-4 py-3 font-medium text-foreground-950">{p.name}</td>
                          <td className="px-4 py-3 text-foreground-600 font-mono text-xs">{p.version}</td>
                          <td className="px-4 py-3 text-foreground-600">{p.transport}</td>
                          <td className="px-4 py-3 text-foreground-600">{p.category}</td>
                          <td className="px-4 py-3">
                            <span className={`text-[11px] px-2 py-1 rounded-full font-medium ${
                              p.status === "Full support" ? "bg-accent-100 text-accent-800" : "bg-secondary-100 text-secondary-800"
                            }`}>
                              {p.status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Codecs */}
              <div>
                <h3 className="text-sm font-semibold text-foreground-800 uppercase tracking-wider mb-4 flex items-center gap-2">
                  <i className="ri-sound-module-line text-primary-600 w-4 h-4 flex items-center justify-center" />
                  Audio Codecs
                </h3>
                <div className="rounded-xl border border-background-200 overflow-hidden">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-background-200 text-left text-xs uppercase tracking-wider text-foreground-500 bg-background-100/60">
                        <th className="px-4 py-3 font-medium">Codec</th>
                        <th className="px-4 py-3 font-medium">Type</th>
                        <th className="px-4 py-3 font-medium">Bitrate</th>
                        <th className="px-4 py-3 font-medium">MOS</th>
                        <th className="px-4 py-3 font-medium">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {codecs.map((c) => (
                        <tr key={c.name} className="border-b border-background-200 last:border-0 hover:bg-background-100/40 transition-colors">
                          <td className="px-4 py-3 font-medium text-foreground-950">{c.name}</td>
                          <td className="px-4 py-3 text-foreground-600">{c.type}</td>
                          <td className="px-4 py-3 text-foreground-600 font-mono text-xs">{c.bitrate}</td>
                          <td className="px-4 py-3">
                            <span className="inline-flex items-center gap-1.5 text-foreground-700">
                              <span className="w-1.5 h-1.5 rounded-full bg-accent-500" />
                              {c.mos}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`text-[11px] px-2 py-1 rounded-full font-medium ${
                              c.status === "Full support" ? "bg-accent-100 text-accent-800" : c.status === "On request" ? "bg-secondary-100 text-secondary-800" : "bg-background-200 text-foreground-600"
                            }`}>
                              {c.status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Monitoring */}
        <section className="py-14 md:py-20 bg-background-100/60 border-y border-background-200">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="mb-10">
              <span className="text-xs uppercase tracking-widest text-accent-700 font-semibold">Monitoring</span>
              <h2 className="mt-3 text-3xl md:text-4xl font-semibold text-foreground-950 leading-tight">
                You see what we see.
              </h2>
              <p className="mt-3 text-foreground-700 max-w-3xl leading-relaxed">
                The same monitoring tools our NOC uses are visible in your customer portal. Real-time dashboards, daily quality digests, CDR archives — full transparency into every route, every destination, every call.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {monitoringTools.map((tool) => (
                <div key={tool.title} className="rounded-xl border border-background-200 bg-background-50 p-6 flex gap-4">
                  <span className="w-12 h-12 rounded-lg bg-primary-100 text-primary-700 flex items-center justify-center shrink-0">
                    <i className={`${tool.icon} text-xl w-6 h-6 flex items-center justify-center`} />
                  </span>
                  <div>
                    <h3 className="text-base font-semibold text-foreground-950 font-heading">{tool.title}</h3>
                    <p className="mt-1.5 text-sm text-foreground-600 leading-relaxed">{tool.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Technical Support */}
        <section className="py-14 md:py-20">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
              <div className="lg:col-span-5">
                <span className="text-xs uppercase tracking-widest text-primary-600 font-semibold">Technical Support</span>
                <h2 className="mt-3 text-3xl md:text-4xl font-semibold text-foreground-950 leading-tight">
                  24/7 human NOC. Not a chatbot.
                </h2>
                <p className="mt-4 text-foreground-700 leading-relaxed max-w-md">
                  Our network operations team follows the sun across three shifts — North America, Europe, and Asia Pacific. You are talking to an engineer who can see your traffic in real time, not a tier-1 triage agent reading a script.
                </p>
                <div className="mt-6 flex flex-wrap gap-2">
                  {["SIP troubleshooting", "Route quality", "CDR reconciliation", "Billing questions", "Emergency failover", "Interconnect tests"].map((topic) => (
                    <span key={topic} className="text-[11px] px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-800 border border-secondary-200 font-medium whitespace-nowrap">
                      {topic}
                    </span>
                  ))}
                </div>
              </div>

              <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-4">
                {supportTiers.map((t) => (
                  <div key={t.label} className="rounded-xl border border-background-200 bg-background-50 p-5">
                    <span className="w-10 h-10 rounded-lg bg-accent-100 text-accent-700 flex items-center justify-center">
                      <i className={`${t.icon} text-lg w-5 h-5 flex items-center justify-center`} />
                    </span>
                    <h3 className="mt-3 text-base font-semibold text-foreground-950 font-heading">{t.label}</h3>
                    <div className="mt-3 space-y-1.5 text-sm">
                      <div className="flex items-center justify-between">
                        <span className="text-foreground-500">Channel</span>
                        <span className="font-medium text-foreground-800">{t.channels}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-foreground-500">Response</span>
                        <span className="font-medium text-foreground-800">{t.response}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-foreground-500">Hours</span>
                        <span className="font-medium text-foreground-800">{t.hours}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-14 md:py-20 bg-primary-500 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-primary-600/40 to-transparent pointer-events-none" />
          <div className="absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full bg-background-50/10 blur-3xl pointer-events-none" />
          <div className="relative max-w-7xl mx-auto px-4 md:px-6 text-center">
            <h2 className="text-3xl md:text-4xl font-semibold text-background-50 font-heading leading-tight">
              Ready to test our network?
            </h2>
            <p className="mt-4 text-background-100 text-base md:text-lg max-w-2xl mx-auto leading-relaxed">
              Schedule a technical call with our engineering team. We will set up a test trunk, walk you through the portal, and run live quality probes against your top destinations — same day.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 bg-background-50 text-primary-700 px-6 py-3.5 rounded-md text-sm font-semibold whitespace-nowrap cursor-pointer hover:bg-background-100 transition-colors"
              >
                Talk to engineering
                <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center" />
              </Link>
              <Link
                to="/coverage"
                className="inline-flex items-center gap-2 bg-background-50/15 text-background-50 border border-background-50/30 px-6 py-3.5 rounded-md text-sm font-semibold whitespace-nowrap cursor-pointer hover:bg-background-50/25 transition-colors"
              >
                View coverage & rates
                <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center" />
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}